import React from 'react';
import get from 'lodash.get';
import moment from 'moment';
import { useQuery, withApollo } from 'react-apollo';
import { flowRight as compose } from 'lodash';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import withActiveLocale from '../../../hocs/withActiveLocale';
import withAnalytics from '../../../hocs/withAnalytics';
import MarkerModel, {
  AirportMarkerModel,
  DrillingRigMarkerModel,
  FuelStationMarkerModel,
  ProjectSiteMarkerModel,
  PropertyMarkerModel,
  SearchPointMarkerModel
} from '../../Marker/Marker.model';

import { MarkerType } from '../../Marker/Marker.constants';
import { QUERY_STATIC_DATA, QUERY_PROPERTY_RESULTS, QUERY_CONNECTED_PROPERTY_RESULTS } from '../../../gql/Map/gql';
import messages from '../MapAssembly/messages';
import MapAssembly from '../MapAssembly';

import * as lodgelink from '../../../constants/messages/lodgeLink';
import PropertyAvailability from '../../../constants/enums/PropertyAvailability';

import { usePropertySearchContext } from '../../../hooks/usePropertySearchContext';
import {
  useMapFiltersContext,
  DEFAULT_PRICE_RANGE_MIN,
  DEFAULT_PRICE_RANGE_MAX,
  DEFAULT_DISTANCE,
  DEFAULT_SAVED_PROPERTY_TYPES,
  DEFAULT_SAVED_LAYERS,
  DEFAULT_SAVED_AMENITIES
} from '../../../hooks/useMapFiltersContext';

const mapLayerIdToMarkerType = [
  MarkerType.STATIC,
  MarkerType.AIRPORT,
  MarkerType.PROJECT_SITE,
  MarkerType.RIG,
  MarkerType.FUEL_STATION,
  MarkerType.SEARCH_POINT
];

// This is just a fallback to prevent errors, probably will require additional refactor on components repo
const LatLngFallback = 0.000000000000001;

const buildQueryParams = (propertySearchInput, queryFilters) => {
  const { searchValue, ...others } = propertySearchInput;
  let queryString = `?location=${searchValue}`;

  Object.keys(others).forEach(key => {
    queryString += `&${key}=${others[key]}`;
  });

  Object.keys(queryFilters.filter).forEach(key => {
    queryString += `&${key}=${queryFilters.filter[key]}`;
  });

  return queryString;
};

// helper to get decimal places back for latlng coordinates
const getDecimalPlacesLength = coordinateValue =>
  coordinateValue.toString().split('.')[1] ? coordinateValue.toString().split('.')[1].length : 0;

// convert latlng based search value into actual float values
const extractLatLngFromValue = searchValue => {
  const searchCoords = searchValue.split(',');
  const searchedLat = parseFloat(searchCoords[0]);
  const searchedLng = parseFloat(searchCoords[1]);
  return { searchedLat, searchedLng };
};

// helper to check if latlng search input matches a property in database
const isLatLngAMatch = (propertyCoordinates, searchValue) => {
  const { lat, lng } = propertyCoordinates;
  const coords = extractLatLngFromValue(searchValue);
  // get the decimal places of the propertys lat and lng
  const propertyLatDecimalPlaces = getDecimalPlacesLength(lat);
  const propertyLngDecimalPlaces = getDecimalPlacesLength(lng);
  // round up the searched lat and lng to the corresponding decimal places
  const adjustedSearchedLat = coords.searchedLat.toFixed(propertyLatDecimalPlaces);
  const adjustedSearchedLng = coords.searchedLng.toFixed(propertyLngDecimalPlaces);

  if (parseFloat(adjustedSearchedLat) === lat && parseFloat(adjustedSearchedLng) === lng) {
    return true;
  }
  return false;
};

// use search point marker to display pin for lat lng and lsd based searches
const buildLatLngMarker = searchValue => {
  const coords = extractLatLngFromValue(searchValue);

  const latLngMarkerModel = new SearchPointMarkerModel({
    id: new Date().getMilliseconds(),
    lat: coords.searchedLat,
    lng: coords.searchedLng,
    label: ' '
  });

  return latLngMarkerModel;
};

// this checks if the search point marker has been instantiated and to be included as part of all markers sent to map
const checkLatLngMarker = markerVariable => {
  if (markerVariable !== null && markerVariable !== undefined) {
    return [markerVariable];
  }
  return [];
};

const buildPropertyModels = (properties = [], intl = {}) => {
  let latSum = 0;
  let lngSum = 0;
  let center = [53.9333, -116.5765];
  const currentMarkers = [];
  const validMarkers = [];
  const invalidMarkers = [];
  // eslint-disable-next-line no-use-before-define
  const showDistanceIn = getShowDistanceIn(properties);

  const propertyModels = properties.map(property => {
    const id = get(property, 'id', 0).toString();
    const timeZone = get(property, 'timeZone');
    const label = get(property, 'name');
    const lat = get(property, 'geo.lat', LatLngFallback);
    const lng = get(property, 'geo.lng', LatLngFallback);
    const imgSrc = get(property, 'cardImage.href');
    const currency = get(property, 'startingPrice.name');
    const price = get(property, 'startingPrice.formatted');
    const ceilingPrice = get(property, 'startingCeilingPrice.formatted', null);
    const ceilingMaxMarkup = get(property, 'startingCeilingParams.percentageMaxMarkup', null);
    const ceilingMaxTrigger = get(property, 'startingCeilingParams.percentageToTriggerMax', null);
    const ceilingGDSMarkup = get(property, 'startingCeilingParams.percentageOnGDS', null);
    const priceValue = get(property, 'startingPrice.value');
    const priceType = get(property, 'startingPrice.type', null);
    const ceilingPriceValue = get(property, 'startingCeilingPrice.value', null);
    const distanceInKms = get(property, 'distance', 0);
    const city = get(property, 'address.city');
    const region = get(property, 'address.region');
    const slug = get(property, 'slug');
    const callForPricing = get(property, 'callForPricing');
    const connected = get(property, 'connected');
    // eslint-disable-next-line no-use-before-define
    const distance = convertDistance(distanceInKms, showDistanceIn);

    const PModel = new PropertyMarkerModel({
      timeZone,
      id,
      label,
      lat,
      lng,
      imgSrc,
      currency,
      price,
      ceilingPrice,
      ceilingMaxMarkup,
      ceilingMaxTrigger,
      ceilingGDSMarkup,
      priceValue,
      priceType,
      ceilingPriceValue,
      connected,
      priceFrequencyLabel: intl.formatMessage(messages.priceFrequencyLabel),
      description: distance
        ? intl.formatMessage(messages.propertyDescriptionWithDistance, {
            city,
            region,
            distance,
            showDistanceIn
          })
        : intl.formatMessage(messages.propertyDescription, {
            city,
            region
          }),
      slug,
      callForPricing: callForPricing ? intl.formatMessage(lodgelink.default.phoneNumber) : null
    });

    // The API was returning invalid lat/lng combinations
    // This code makes sure they have been defined and are within a valid range
    // Any properties with invalid lat/lng is discarded as a "invalidMarker"
    if (lat !== LatLngFallback && lng !== LatLngFallback && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      latSum += lat;
      lngSum += lng;
      currentMarkers.push([lat, lng]);
      validMarkers.push(PModel);
    } else {
      invalidMarkers.push(PModel);
    }

    return PModel;
  });

  if (validMarkers.length) {
    const latAvg = latSum / validMarkers.length;
    const lngAvg = lngSum / validMarkers.length;

    if (latAvg && lngAvg) {
      center = [latAvg, lngAvg];
    }
  }

  // Log a warning if properties with invalid lat/lng were found
  if (invalidMarkers.length) {
    console.warn('Invalid Markers:', invalidMarkers); // eslint-disable-line no-console
  }

  return {
    propertyModels,
    center,
    currentMarkers,
    validMarkers
  };
};

const buildConnectedPropertyModels = (properties, markers, connections, propertyIds, sortBy, sort, isAdmin) => {
  const newProperties = [...properties];
  const newMarkers = [...markers];
  const allIds = [...propertyIds];

  allIds.forEach(id => {
    const update = {};
    const propIndex = newProperties.findIndex(p => parseInt(p.id, 10) === id);
    const markerIndex = newMarkers.findIndex(m => parseInt(m.id, 10) === id);
    const connectionIndex = connections.findIndex(c => c.propertyId === id);

    if (connectionIndex !== -1) {
      // connection returned
      const connection = connections[connectionIndex];

      update.availability = PropertyAvailability[connection.availability];
      if (update.availability === PropertyAvailability.SOLD_OUT) {
        // got no info from connection
        update.callForPricing = !isAdmin;

        newProperties[propIndex] = new PropertyMarkerModel({
          ...newProperties[propIndex],
          ...update
        });
        newMarkers[markerIndex] = new PropertyMarkerModel({
          ...newMarkers[markerIndex],
          ...update
        });
      } else {
        if (connection.lowestRate) {
          const { value } = connection.lowestRate;
          update.priceValue = value;
          update.strikeoutPrice = null;
          // strikeout rate display
          if (connection.lowestRacRate) {
            if (connection.lowestRacRate.value > value && connection.lowestRacRate.type !== 'LNR') {
              update.strikeoutPrice = connection.lowestRacRate.formatted;
            }
          }

          update.strikeoutPrice = connection.ceilingRateAvailabile ? null : update.strikeoutPrice;
          update.price = `$${value.toFixed(2)}`;
          newProperties[propIndex] = new PropertyMarkerModel({
            ...newProperties[propIndex],
            ...update
          });
        }
        newMarkers[markerIndex] = new PropertyMarkerModel({
          ...newMarkers[markerIndex],
          ...update
        });
      }
    } else {
      // no connection
      newProperties[propIndex] = new PropertyMarkerModel({
        ...newProperties[propIndex],
        connected: false
      });
      newMarkers[markerIndex] = new PropertyMarkerModel({
        ...newMarkers[markerIndex],
        connected: false
      });
    }
  });

  // SORTING

  if (sortBy === 'Price') {
    // sort by price
    newProperties.sort((aProp, bProp) => {
      if (aProp.priceValue < bProp.priceValue) {
        return sort === 'ASC' ? -1 : 1;
      }
      if (aProp.priceValue > bProp.priceValue) {
        return sort === 'ASC' ? 1 : -1;
      }
      return 0;
    });
  }
  newProperties.sort((aProp, bProp) => {
    if (aProp.availability === 'sold-out' && bProp.availability !== 'sold-out') {
      return 1;
    }
    if (aProp.availability !== 'sold-out' && bProp.availability === 'sold-out') {
      return -1;
    }

    return 0;
  });
  return { newProperties, newMarkers };
};

const buildConnectedErrorPropertyModels = (propertyIds, properties, markers, loading) => {
  const newProperties = [...properties];
  const newMarkers = [...markers];
  if (!loading) {
    propertyIds.forEach(propertyId => {
      const propIndex = newProperties.findIndex(p => p.id === propertyId.toString());
      const markerIndex = newMarkers.findIndex(m => m.id === propertyId.toString());
      if (propIndex !== -1 && markerIndex !== -1) {
        newProperties[propIndex] = new PropertyMarkerModel({
          ...newProperties[propIndex],
          connected: false
        });
        newMarkers[markerIndex] = new PropertyMarkerModel({
          ...newMarkers[markerIndex],
          connected: false
        });
      }
    });
  }
  return { newProperties, newMarkers };
};

const buildPOIMarkerModels = POIList =>
  // Each inner array corresponds to type of MarkerModel, refer to 'mapIdsToMarkerModel' above

  POIList.map(poi => {
    const id = get(poi, 'id');
    const label = get(poi, 'name');
    const lat = get(poi, 'geo.lat', LatLngFallback);
    const lng = get(poi, 'geo.lng', LatLngFallback);
    const layerId = get(poi, 'layer.id');
    const metadata = get(poi, 'metadata');

    const type = mapLayerIdToMarkerType[layerId] || MarkerType.STATIC;
    let newPOIMarker;

    const baseProps = {
      id,
      label,
      lat,
      lng,
      type
    };

    let metaDataFilter;

    switch (type) {
      case MarkerType.FUEL_STATION:
        newPOIMarker = new FuelStationMarkerModel({ ...baseProps, location: label });
        break;
      case MarkerType.RIG:
        newPOIMarker = new DrillingRigMarkerModel({ ...baseProps, owner: label });
        newPOIMarker.owner = label;
        metaDataFilter = ['operator', 'rig_number', 'location', 'uwi', 'well_type'];
        break;
      case MarkerType.PROJECT_SITE:
        newPOIMarker = new ProjectSiteMarkerModel(baseProps);
        break;
      case MarkerType.AIRPORT:
        newPOIMarker = new AirportMarkerModel(baseProps);
        break;
      // case MarkerType.FIRST_NATION:
      //   newPOIMarker = new FirstNationsMarkerModel(baseProps);
      //   break;
      default:
        newPOIMarker = new MarkerModel(baseProps);
    }

    if (metadata) {
      const additionalProperties = JSON.parse(metadata);
      newPOIMarker.fields = [];

      metaDataFilter.forEach(key => {
        if (additionalProperties[key]) {
          newPOIMarker.fields.push({
            name: key.replace(/_/g, ' '),
            value: additionalProperties[key]
          });
        }
      });
    }

    return newPOIMarker;
  });

// Temporary solution for finding distance measure, until we use locale site wide.
const getShowDistanceIn = properties => {
  const propertyCountries = {};
  let showDistanceIn = 'kms';

  // eslint-disable-next-line array-callback-return
  properties.map(property => {
    const country = get(property, 'address.country');
    if (country) {
      propertyCountries[country] = propertyCountries[country] ? propertyCountries[country] + 1 : 1;
    }
  });

  if (Object.keys(propertyCountries).length) {
    const maxPropertiesInCountry = Object.keys(propertyCountries).reduce((a, b) =>
      propertyCountries[a] > propertyCountries[b] ? a : b
    );
    showDistanceIn = maxPropertiesInCountry === 'United States' ? 'miles' : 'kms';
  }

  return showDistanceIn;
};

// Convert distance based on showDistanceIn i.e. kms or miles
const convertDistance = (distanceInKms, showDistanceIn) =>
  Math.ceil(distanceInKms / (showDistanceIn === 'kms' ? 1 : 1.60934));

function MapAssemblyContainer(props) {
  const { data: staticData } = useQuery(QUERY_STATIC_DATA, {
    ssr: false
  });

  const { layers, amenities } = staticData || {};

  const { searchCriteria, setSearchCriteria } = usePropertySearchContext();
  const { mapFilters: mapFiltersContext, setMapFilters } = useMapFiltersContext();

  const propertySearchInput = {
    checkin: searchCriteria?.startDate,
    checkout: searchCriteria?.endDate,
    numberRooms: searchCriteria?.propertySearchInput?.numberRooms,
    searchType: searchCriteria?.propertySearchInput?.searchType,
    searchValue: searchCriteria?.propertySearchInput?.searchValue
  };

  const propertyTypes = [
    // Set a placeholder for now until we have a supported query
    {
      name: 'Hotels',
      icon: 'hotel',
      id: 2
    },
    {
      name: 'Lodges',
      icon: 'hotel',
      id: 1
    }
  ];

  const savedAmenitiesHash = mapFiltersContext?.savedAmenities.reduce((accum, next) => {
    accum[next] = true;
    return accum;
  }, {});

  const savedPropertyTypesHash = mapFiltersContext?.savedPropertyTypes.reduce((accum, next) => {
    accum[next] = true;
    return accum;
  }, {});

  const savedLayersHash = mapFiltersContext?.savedLayers.reduce((accum, next) => {
    accum[next] = true;
    return accum;
  }, {});

  let numFilters = mapFiltersContext?.savedAmenities.length; // Note: This calculation will change if DEFAULT_AMENITIES is NOT empty

  if (mapFiltersContext?.savedPriceRangeMin !== DEFAULT_PRICE_RANGE_MIN) {
    numFilters += 1;
  }

  if (mapFiltersContext?.savedPriceRangeMax !== DEFAULT_PRICE_RANGE_MAX) {
    numFilters += 1;
  }

  if (mapFiltersContext?.savedDistance !== DEFAULT_DISTANCE) {
    numFilters += 1;
  }

  if (mapFiltersContext?.savedPropertyTypes.length) {
    numFilters += DEFAULT_SAVED_PROPERTY_TYPES.length - mapFiltersContext.savedPropertyTypes.length;
  }

  const queryMapFiltersData = {
    queryFilters: {
      filter: {
        propertyTypes: mapFiltersContext?.savedPropertyTypes,
        minRates: mapFiltersContext?.savedPriceRangeMin,
        maxRates: mapFiltersContext?.savedPriceRangeMax,
        minRoomsAvailable: searchCriteria?.propertySearchInput?.numberRooms,
        layers: mapFiltersContext?.savedLayers,
        amenity: mapFiltersContext?.savedAmenities,
        distanceFrom: mapFiltersContext?.savedDistance,
        distanceType: 'Kilometers'
      }
    },
    mapFilters: {
      defaults: {
        layers: layers
          ?.filter(layer => layer.active)
          .map(layer => ({
            layerId: layer.id,
            label: layer.name,
            icon: layer.icon,
            selected: DEFAULT_SAVED_LAYERS.indexOf(layer.id) > -1
          })),
        propertyTypes: propertyTypes.map(propertyType => ({
          label: propertyType.name,
          layerId: propertyType.id,
          icon: propertyType.icon,
          selected: DEFAULT_SAVED_PROPERTY_TYPES.indexOf(propertyType.id) > -1
        })),
        priceRangeMin: DEFAULT_PRICE_RANGE_MIN,
        priceRangeMax: DEFAULT_PRICE_RANGE_MAX,
        distance: DEFAULT_DISTANCE,
        amenities: amenities
          ?.filter(amenity => amenity.filterable)
          .map(amenity => ({
            label: amenity.name,
            layerId: amenity.code,
            icon: amenity.iconCode || 'info',
            selected: DEFAULT_SAVED_AMENITIES.indexOf(amenity.code) > -1
          }))
      },
      filtersContainerState: {
        amenities: amenities
          ?.filter(amenity => amenity.filterable)
          .map(amenity => ({
            label: amenity.name,
            layerId: amenity.code,
            icon: amenity.iconCode || 'info',
            selected: savedAmenitiesHash[amenity.code]
          })),
        propertyTypes: propertyTypes.map(propertyType => ({
          label: propertyType.name,
          layerId: propertyType.id,
          icon: propertyType.icon,
          selected: savedPropertyTypesHash[propertyType.id]
        })),
        startDate: searchCriteria?.startDate,
        endDate: searchCriteria?.endDate,
        numRooms: searchCriteria?.propertySearchInput?.numberRooms,
        distance: mapFiltersContext?.savedDistance,
        priceRangeMin: mapFiltersContext?.savedPriceRangeMin,
        priceRangeMax: mapFiltersContext?.savedPriceRangeMax
      },
      filtersContainerProps: {
        numFilters,
        savedStartDate: searchCriteria?.startDate,
        savedEndDate: searchCriteria?.endDate,
        savedNumRooms: searchCriteria?.propertySearchInput?.numberRooms,
        savedDistance: mapFiltersContext?.savedDistance,
        savedPriceRangeMin: mapFiltersContext?.savedPriceRangeMin,
        savedPriceRangeMax: mapFiltersContext?.savedPriceRangeMax,
        location: searchCriteria?.propertySearchInput?.searchValue,
        labelStartDate: moment(searchCriteria?.startDate, 'YYYY-MM-DD')
          .utc()
          .format('MMM DD'),
        labelEndDate: moment(searchCriteria?.endDate, 'YYYY-MM-DD')
          .utc()
          .format('MMM DD'),
        dateProps: {
          initialStartDate: searchCriteria?.startDate,
          initialEndDate: searchCriteria?.endDate
        }
      },
      mapLayerPanelState: {
        layers: layers
          ?.filter(layer => layer.active)
          .map(layer => ({
            layerId: layer.id,
            label: layer.name,
            icon: layer.icon,
            selected: savedLayersHash[layer.id]
          }))
      },
      mapLayerIdToMarkerType
    }
  };

  propertySearchInput.numberRooms = parseInt(propertySearchInput.numberRooms, 10);
  queryMapFiltersData.queryFilters.filter.minRoomsAvailable = parseInt(
    queryMapFiltersData.queryFilters.filter.minRoomsAvailable,
    10
  );
  queryMapFiltersData.queryFilters.filter.propertyTypes = queryMapFiltersData.queryFilters.filter.propertyTypes.map(
    propertyType => parseInt(propertyType, 10)
  );
  queryMapFiltersData.queryFilters.filter.layers = queryMapFiltersData.queryFilters.filter.layers.map(layer =>
    parseInt(layer, 10)
  );

  if (!propertySearchInput.searchType) {
    delete propertySearchInput.searchType;
  }

  const {
    data: propertyResultsData,
    refetch: refetchPropertyResults,
    error: propertyResultsError,
    loading: propertyResultsLoading,
    variables: propertyResultsVariables
  } = useQuery(QUERY_PROPERTY_RESULTS, {
    variables: {
      propertySearchInput: propertySearchInput.searchValue
        ? propertySearchInput
        : { ...propertySearchInput, searchValue: '', searchType: 'Address' },
      options: propertySearchInput.searchValue
        ? {
            ...queryMapFiltersData.queryFilters,
            orderby: mapFiltersContext?.orderBy,
            order: mapFiltersContext?.order
          }
        : {
            filter: { ...queryMapFiltersData.queryFilters.filter, distanceFrom: 10 },
            orderby: mapFiltersContext?.orderBy,
            order: mapFiltersContext?.order
          },
      skip: !propertySearchInput.searchValue
    },
    ssr: false,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      const { router } = props;
      const { asPath } = router;
      const queryAt = asPath.indexOf('?');
      const newPath = queryAt === -1 ? asPath : asPath.substring(0, queryAt);
      const queryParams = buildQueryParams(propertySearchInput, queryMapFiltersData.queryFilters);
      const {
        history,
        document: { title }
      } = window;
      history.replaceState(history.state, title, newPath + queryParams);
    }
  });

  const { properties, pointsOfInterest, searchValue, favouriteProperties } = propertyResultsData?.mapSearch || {
    properties: [],
    pointsOfInterest: [],
    favouriteProperties: [],
    refetch: () => {}
  };

  const doesReturnedValueMatchLatLngRegex = searchValue
    ? searchValue.match(/^(-?[0-9]{1,3}\.[0-9]{2,}, ?-?[0-9]{1,3}\.[0-9]{2,})/g)
    : false;

  let propertyParams;
  let latLngMarker;

  if (doesReturnedValueMatchLatLngRegex) {
    const [selectedProperty] = properties;
    if (selectedProperty) {
      // checks if first returned property is exact match for users search value
      const doLatLngsMatch = isLatLngAMatch(selectedProperty.geo, propertySearchInput.searchValue);
      if (doLatLngsMatch) {
        propertyParams = [selectedProperty];
      } else {
        propertyParams = properties;
        latLngMarker = buildLatLngMarker(searchValue);
      }
    } else {
      propertyParams = properties;
      latLngMarker = buildLatLngMarker(searchValue);
    }
  } else {
    propertyParams = properties;
  }

  const { intl, auth } = props;

  // Build initial property and POI models from the base property results
  const {
    propertyModels: initialPropertyModels,
    validMarkers: initialValidMarkers,
    center,
    currentMarkers
  } = buildPropertyModels(propertyParams, intl);
  const POIMarkerModels = buildPOIMarkerModels(pointsOfInterest);

  const propertyIds = initialPropertyModels?.filter(p => p.connected).map(p => parseInt(p.id, 10));

  const {
    data: connectedPropertyResultsData,
    loading: connectedPropertyResultsLoading,
    variables: connectedPropertyResultsVariables,
    refetch: refetchConnectedPropertiesData
  } = useQuery(QUERY_CONNECTED_PROPERTY_RESULTS, {
    ssr: false,
    variables: {
      propertyIds,
      checkin: searchCriteria?.startDate,
      checkout: searchCriteria?.endDate,
      skip: !propertyIds?.length
    },
    notifyOnNetworkStatusChange: true
  });

  const isAdmin = auth?.me?.admin;
  const currentOptions = propertyResultsVariables?.options;

  const { newProperties, newMarkers } =
    connectedPropertyResultsData?.getConnectedProperties && !connectedPropertyResultsLoading
      ? buildConnectedPropertyModels(
          initialPropertyModels,
          [...initialValidMarkers, ...POIMarkerModels, ...checkLatLngMarker(latLngMarker)],
          connectedPropertyResultsData?.getConnectedProperties,
          connectedPropertyResultsVariables?.propertyIds,
          currentOptions?.orderby,
          currentOptions?.order,
          isAdmin
        )
      : buildConnectedErrorPropertyModels(
          connectedPropertyResultsVariables?.propertyIds || [],
          initialPropertyModels,
          [...initialValidMarkers, ...POIMarkerModels, ...checkLatLngMarker(latLngMarker)],
          connectedPropertyResultsLoading
        );

  const mapAssemblyProps = {
    ...props,
    ...queryMapFiltersData,
    setSearchCriteria,
    setMapFilters,
    propertySearchInput,
    roomCount: searchCriteria?.roomCount,
    startDate: searchCriteria?.startDate,
    endDate: searchCriteria?.endDate,
    loading: propertyResultsLoading,
    propertiesLoading: propertyResultsLoading,
    properties: newProperties,
    markers: newMarkers,
    propertyCount: newProperties?.length || 0,
    favouriteProperties,
    mapSettings: {
      center,
      zoom: 7,
      mapLayers: mapLayerIdToMarkerType
    },
    currentOptions,
    currentSearch: propertyResultsVariables?.propertySearchInput?.searchValue,
    currentMarkers,
    error: propertyResultsError,
    noResults:
      !propertyResultsVariables?.skip &&
      !propertyResultsError &&
      !propertyResultsLoading &&
      !connectedPropertyResultsLoading &&
      properties.length === 0,
    refetchConnectedProperties: (newPropertySearchInputVars, refreshedProperties) => {
      const refreshedPropertyIds = refreshedProperties.filter(p => p.connected).map(p => parseInt(p.id, 10));
      refetchConnectedPropertiesData({
        propertyIds: refreshedPropertyIds,
        checkin: newPropertySearchInputVars.checkin,
        checkout: newPropertySearchInputVars.checkout,
        skip: refreshedPropertyIds.length === 0
      });
    },
    refetchPropertyResults
  };

  return <MapAssembly {...mapAssemblyProps} />;
}

MapAssemblyContainer.propTypes = {
  intl: PropTypes.shape({}).isRequired,
  router: PropTypes.shape({
    asPath: PropTypes.string
  }),
  auth: PropTypes.shape({
    me: PropTypes.shape({
      admin: PropTypes.bool
    })
  }),
  client: PropTypes.shape({}),
  analytics: PropTypes.shape({})
};

MapAssemblyContainer.defaultProps = {
  router: null,
  auth: null,
  client: null,
  analytics: null
};

/**
 * MapContainer Provides the data and mutations required for the Featured Properties
 * section on the homepage.
 */

const MapAssemblyWithData = compose(
  withApollo,
  withAnalytics(),
  withActiveLocale,
  injectIntl
)(MapAssemblyContainer);

export default MapAssemblyWithData;
